import cx from 'classix';
import { ForwardedRef, forwardRef } from 'react';
import { AriaCheckboxProps, mergeProps, useCheckbox, useFocusRing, useObjectRef } from 'react-aria';
import {
  Control,
  Controller,
  FieldError,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';
import { useToggleState } from 'react-stately';

/**
 * Styled select box component - used for checkbox and radio button
 */
export const SelectBox = (props: {
  isFocusVisible?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  isIndeterminate?: boolean;
  size: 'sm' | 'md';
  type: 'dot' | 'check';
}) => {
  const svgSize = props.size === 'sm' ? '12' : '14';
  return (
    <div
      className={cx(
        'border w-4 h-4 flex items-center justify-center shrink-0',
        props.size === 'sm' ? 'w-4 h-4' : 'w-5 h-5',
        props.type === 'dot' ? 'rounded-full' : 'rounded mt-0.5',
        props.isSelected || props.isIndeterminate ?
          'bg-brand-500 border-brand-500 hover:bg-brand-600 hover:border-brand-600'
        : 'border-gray-200 hover:border-gray-300',
        props.isFocusVisible && !props.isSelected && 'ring-gray',
        props.isFocusVisible && props.isSelected && 'ring-brand',
        props.isDisabled && 'bg-gray-50 hover:bg-gray-50 border-gray-200 hover:border-gray-200',
      )}
    >
      {props.isSelected && props.type === 'dot' && (
        <div
          className={cx(
            'rounded-full w-1.5 h-1.5',
            props.isDisabled ? 'bg-gray-200' : 'bg-white',
            props.size === 'sm' ? 'w-1.5 h-1.5' : 'w-2 h-2',
          )}
        ></div>
      )}
      {props.isIndeterminate && !props.isSelected && (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          preserveAspectRatio='xMidYMid meet'
          width={svgSize}
          height={svgSize}
          viewBox={'0 0 24 24'}
          fill='none'
          className={cx('stroke-[2.5px]', props.isDisabled ? 'stroke-gray-200' : 'stroke-white')}
        >
          <path d='M5 12H19' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
      )}
      {props.isSelected && props.type === 'check' && (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          preserveAspectRatio='xMidYMid meet'
          width={svgSize}
          height={svgSize}
          viewBox={'0 0 24 24'}
          fill='none'
          className={cx('stroke-[2.5px]', props.isDisabled ? 'stroke-gray-200' : 'stroke-white')}
        >
          <path d='M20 6L9 17L4 12' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
      )}
    </div>
  );
};

type CheckBoxProps = AriaCheckboxProps & {
  description?: string | JSX.Element;
  size: 'sm' | 'md';
  additionalClassnames?: string;
};

export const CheckBox = forwardRef((props: CheckBoxProps, ref?: ForwardedRef<HTMLInputElement>) => {
  const state = useToggleState(props);
  const inputRef = useObjectRef(ref);
  const { inputProps } = useCheckbox(props, state, inputRef);
  const { isFocusVisible, focusProps } = useFocusRing();
  return (
    <label className={cx('flex items-start gap-x-2', props.additionalClassnames)}>
      <div className='hidden'>
        <input ref={inputRef} {...mergeProps(inputProps, focusProps)} />
      </div>
      <SelectBox
        size={props.size}
        isIndeterminate={props.isIndeterminate}
        isFocusVisible={isFocusVisible}
        isDisabled={props.isDisabled}
        isSelected={state.isSelected}
        type='check'
      />
      <div className='flex flex-col'>
        <div className={`text-${props.size} font-medium text-gray-700`}>{props.children}</div>
        <div className={`text-${props.size} text-gray-500`}>{props.description}</div>
      </div>
    </label>
  );
});

export const FormsCheckBox = <FData extends FieldValues>(
  props: CheckBoxProps & {
    control: Control<FData>;
    name: Path<FData>;
    rules?: RegisterOptions<FData>;
    formsError?: FieldError;
  },
) => {
  return (
    <Controller
      control={props.control}
      name={props.name}
      rules={props.rules}
      render={({ field }) => (
        <CheckBox
          {...field}
          size={props.size}
          isIndeterminate={props.isIndeterminate}
          isDisabled={props.isDisabled}
          isSelected={field.value}
          description={props.description}
        >
          {props.children}
        </CheckBox>
      )}
    />
  );
};
